import React, { useState } from 'react';
import { Box, Drawer, IconButton, Typography, Stack, alpha, Radio, Divider, Button, RadioGroup, FormControlLabel, Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Yup from 'yup';
import { Formik } from 'formik';
import LoadingButton from 'components/@extended/LoadingButton';
import Loader from 'components/Loader';
import { textSizeByBreakpoint } from 'utils/helper';
import { PlusOutlined, MinusOutlined, CreditCardOutlined, WalletOutlined, ArrowLeftOutlined, CloseOutlined } from '@ant-design/icons';
import { useBillingClient } from 'client/client-hook';
import useAuth from 'hooks/useAuth';
import { CreateSubscriptionReq, CreateSubscriptionReply, ListPricesReq, ListPricesReply, Price, CardSetUpIntentReq, CardSetUpIntentReply, CreateCryptoPaymentRequest, CreateCryptoPaymentReply, ListCardsReply, SetDefaultPaymentMethodReq } from 'proto/billing-manager_pb';
import { DispatchSnackbarService } from 'services';
import { useDispatch } from 'store';
import { useUserStripeState } from 'hooks/useUserStripe';
import StripeAccountUnavailable from 'components/StripeAccountUnavailable';
import { getUserStripe } from 'lib/stripe/userStripe';
import { config } from 'env';
import { getStripeErrorMessage } from 'services/snackbar/dispatch-snackbar.service';
import { trackPurchase } from 'utils/gtm';
import StripeCheckout from 'sections/account-settings/components/StripeCheckout';
import { PreSelectedPlan } from 'contexts/UpgradeDrawerContext';
import { Elements } from '@stripe/react-stripe-js';
import visaCard from '../../../../assets/images/icons/visa-card.svg';
import masterCard from '../../../../assets/images/icons/master-card.svg';
import amexCard from '../../../../assets/images/icons/american-express.svg';
import discoverCard from '../../../../assets/images/icons/discover.svg';
import dinersClubCard from '../../../../assets/images/icons/diners_club.svg';
import jcbCard from '../../../../assets/images/icons/jcb.svg';
import unionPayCard from '../../../../assets/images/icons/unionpay.svg';

const iconMap: Record<string, string> = {
  visa: visaCard,
  mastercard: masterCard,
  amex: amexCard,
  discover: discoverCard,
  diners: dinersClubCard,
  jcb: jcbCard,
  unionpay: unionPayCard
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onCardsUpdated?: (cards: any[]) => void;
  subscriptions?: any[];
  onManageSubscription?: () => void;
  creditBalance?: number;
  paymentCards?: any[];
  overdueSubscription?: any;
  onRenewOverdue?: () => void;
  // Supplied by MainLayout; this legacy drawer doesn't act on it.
  preSelectedPlan?: PreSelectedPlan;
};

// ANALYZER PURCHASE - Set to true to enable Analyzer billing in checkout
const SHOW_ANALYZER_PURCHASE = true;

const UpgradePlanDrawer = ({ isOpen, onClose, onSuccess, onCardsUpdated, subscriptions = [], onManageSubscription, creditBalance = 0, paymentCards = [], overdueSubscription, onRenewOverdue }: Props) => {
  const { stripe, error: stripeError, retryable: stripeRetryable, retry: retryStripe } = useUserStripeState();
  const theme = useTheme();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const getBillingClient = useBillingClient();
  const service = new DispatchSnackbarService();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<'plan' | 'configure' | 'payment'>('plan');
  const [accountsQuantity, setAccountsQuantity] = useState(1);
  const [standardAccountsQuantity, setStandardAccountsQuantity] = useState(2);
  const [premiumAccountsQuantity, setPremiumAccountsQuantity] = useState(0);
  const [selectedPlanType, setSelectedPlanType] = useState<'copier' | 'dedi' | null>(null);
  const [paymentType, setPaymentType] = useState<'card' | 'crypto'>('card');
  const [selectedCard, setSelectedCard] = useState<string>('');
  const [showAddCard, setShowAddCard] = useState(false);
  const [clientSecret, setClientSecret] = useState<undefined | string>(undefined);
  const [confirmSaveCard, setConfirmSaveCard] = useState(false);
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [saveCardLoading, setSaveCardLoading] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false });
  const [localPaymentCards, setLocalPaymentCards] = useState<any[]>([]);
  const [pendingPurchase, setPendingPurchase] = useState(false);
  const formValuesRef = React.useRef<typeof initialValues | null>(null);

  // Sync local cards with props
  React.useEffect(() => {
    setLocalPaymentCards(paymentCards);
  }, [paymentCards]);

  const resetDrawer = () => {
    setLoading(false);
    setCurrentStep('plan');
    setAccountsQuantity(1);
    setStandardAccountsQuantity(2);
    setPremiumAccountsQuantity(0);
    setSelectedPlanType(null);
    setPaymentType('card');
    setSelectedCard('');
    setShowAddCard(false);
    setClientSecret(undefined);
    setConfirmSaveCard(false);
    setIsCardComplete(false);
    setStripeLoaded(false);
    setSaveCardLoading(false);
    setTooltipPos({ x: 0, y: 0, visible: false });
    setPendingPurchase(false);
    formValuesRef.current = null;
  };

  // Auto-select default card
  React.useEffect(() => {
    const defaultCard = localPaymentCards.find((card: any) => card.isDefault?.value);
    if (defaultCard && !selectedCard) {
      setSelectedCard(defaultCard.paymentMethodId);
    }
  }, [localPaymentCards, selectedCard]);

  // Fetch setup intent when entering payment step or when showing add card
  React.useEffect(() => {
    if (currentStep === 'payment' && user?.tcId && !clientSecret) {
      fetchSetupIntent();
    }
  }, [currentStep, user?.tcId]);

  // Also fetch setup intent when clicking Add Card (in case it wasn't fetched yet)
  React.useEffect(() => {
    if (showAddCard && user?.tcId && !clientSecret) {
      fetchSetupIntent();
    }
  }, [showAddCard, user?.tcId]);

  const fetchSetupIntent = async () => {
    if (!user?.tcId) return;
    try {
      const client = await getBillingClient();
      const req: CardSetUpIntentReq.AsObject = { userId: user.tcId };
      const res: any = await client.cardSetUpIntent(req);
      const result = res as CardSetUpIntentReply.AsObject;
      setClientSecret(result?.clientSecret);
    } catch (error) {
      console.error('Failed to fetch setup intent:', error);
    }
  };

  const handleCardSaved = async (setupIntent: any) => {
    if (!user?.tcId) return;
    console.log('Card saved, setupIntent:', setupIntent);

    if (!setupIntent || !setupIntent.payment_method) {
      console.error('No payment method in setupIntent');
      setPendingPurchase(false);
      return;
    }

    const paymentMethodId = setupIntent.payment_method;
    
    setShowAddCard(false);
    setConfirmSaveCard(false);
    setSaveCardLoading(true);
    
    // Poll for the new card to appear in the list
    const maxAttempts = 10;
    const pollInterval = 1500;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      
      try {
        const client = await getBillingClient();
        const cardsResp = await client.listCards({ userId: user.tcId });
        const result = cardsResp as ListCardsReply.AsObject;
        
        // Check if the new card is in the list
        const newCard = result.cardsList.find((x) => x.paymentMethodId === paymentMethodId);
        
        if (newCard) {
          console.log('Found new card:', newCard);
          
          // Set it as default
          const req: SetDefaultPaymentMethodReq.AsObject = { userId: user.tcId, cardId: newCard.id, paymentMethodId: '' };
          await client.setDefaultPaymentMethod(req);
          
          // Update local cards list
          setLocalPaymentCards(result.cardsList);
          setSelectedCard(newCard.paymentMethodId);
          
          // Notify parent to update its cards list
          if (onCardsUpdated) {
            onCardsUpdated(result.cardsList);
          }
          
          // Reset clientSecret for next add card
          setClientSecret(undefined);
          setSaveCardLoading(false);
          
          // If there's a pending purchase, execute it now with the new card
          if (pendingPurchase && formValuesRef.current) {
            service.dispatchSnackbar(dispatch, 'Card added successfully. Processing payment...', 'alert', 'success', 2000);
            setPendingPurchase(false);
            // Execute purchase with the new card's payment method ID
            await executePurchase(formValuesRef.current, newCard.paymentMethodId);
            formValuesRef.current = null;
          } else {
            service.dispatchSnackbar(dispatch, 'Card added successfully', 'alert', 'success', 3000);
          }
          return;
        }
      } catch (error) {
        console.error('Failed to fetch cards:', error);
      }
    }
    
    // If we reach here, the card wasn't found after polling
    setSaveCardLoading(false);
    setPendingPurchase(false);
    formValuesRef.current = null;
    service.dispatchSnackbar(dispatch, 'Card saved but may take a moment to appear', 'alert', 'warning', 3000);
  };

  const handleSaveCardClick = () => {
    setConfirmSaveCard(true);
    setTimeout(() => setConfirmSaveCard(false), 100);
  };

  const handleStripeChange = (event: any) => {
    setIsCardComplete(event.complete);
  };

  // Reset state when drawer closes
  React.useEffect(() => {
    if (!isOpen) {
      setCurrentStep('plan');
      setAccountsQuantity(2);
      setPaymentType('card');
    }
  }, [isOpen]);

  // Helper function to check if user already has this specific subscription
  const hasSubscription = (planId: string): boolean => {
    const isCopier = planId.startsWith('copier-');
    const isDedi = planId.startsWith('analyzer-');
    const isAnalytics = planId.startsWith('analytics-');
    const interval = planId.split('-')[1]; // monthly, quarterly, semiannually, annual
    
    console.log(`Checking hasSubscription for planId: ${planId}`, {
      isCopier,
      isDedi,
      isAnalytics,
      interval,
      subscriptions: subscriptions.map(s => ({
        productName: s.productName,
        interval: s.interval,
        intervalCount: s.intervalCount
      }))
    });
    
    const result = subscriptions.some(sub => {
      const productNameLower = sub.productName?.toLowerCase() || '';
      const subInterval = sub.interval?.toLowerCase() || '';
      // Convert to number to handle both string and number values from API
      const subIntervalCount = Number(sub.intervalCount) || 1;
      
      if (isCopier) {
        // Check if user has a TC Copier subscription with this specific interval
        const isCopierProduct = productNameLower.includes('copier') || productNameLower.includes('account');
        if (!isCopierProduct) return false;
        
        // Map interval names using both interval and intervalCount
        if (interval === 'monthly' && subInterval === 'month' && subIntervalCount === 1) return true;
        if (interval === 'quarterly' && subInterval === 'month' && subIntervalCount === 3) return true;
        if (interval === 'semiannually' && subInterval === 'month' && subIntervalCount === 6) return true;
        if (interval === 'annual' && subInterval === 'year' && subIntervalCount === 1) return true;
        return false;
      } else if (isAnalytics) {
        // Check if user has an Analyzer (Advanced Analytics) subscription with this specific interval
        // Note: productName should be "Analyzer" for the advanced analytics product
        const isAnalyticsProduct = productNameLower === 'analyzer' || productNameLower.includes('advanced analytics');
        if (!isAnalyticsProduct) return false;
        
        // Map interval names using both interval and intervalCount
        if (interval === 'monthly' && subInterval === 'month' && subIntervalCount === 1) return true;
        if (interval === 'quarterly' && subInterval === 'month' && subIntervalCount === 3) return true;
        if (interval === 'semiannually' && subInterval === 'month' && subIntervalCount === 6) return true;
        if (interval === 'annual' && subInterval === 'year' && subIntervalCount === 1) return true;
        return false;
      } else {
        // Check if user has a Dedicated Environment subscription with this specific interval
        const isDediProduct = productNameLower.includes('dedicated') || productNameLower.includes('environment');
        if (!isDediProduct) return false;
        
        // Map interval names using both interval and intervalCount
        if (interval === 'monthly' && subInterval === 'month' && subIntervalCount === 1) return true;
        if (interval === 'quarterly' && subInterval === 'month' && subIntervalCount === 3) return true;
        if (interval === 'semiannually' && subInterval === 'month' && subIntervalCount === 6) return true;
        if (interval === 'annual' && subInterval === 'year' && subIntervalCount === 1) return true;
        return false;
      }
    });
    
    console.log(`hasSubscription result for ${planId}:`, result);
    return result;
  };

  // Helper function to check if user has any TC Copier subscription
  const hasCopierSubscription = (): boolean => {
    return subscriptions.some(sub => {
      const productNameLower = sub.productName?.toLowerCase() || '';
      return productNameLower.includes('copier') || productNameLower.includes('account') || productNameLower.includes('standard');
    });
  };

  // Helper function to check if user has any Analyzer subscription (only 1 allowed at a time)
  const hasAnyAnalyzerSubscription = (): boolean => {
    return subscriptions.some(sub => {
      const productNameLower = sub.productName?.toLowerCase() || '';
      return productNameLower === 'analyzer' || productNameLower.includes('advanced analytics');
    });
  };

  // Helper function to check if a planId matches the overdue subscription
  const isOverduePlan = (planId: string): boolean => {
    if (!overdueSubscription) return false;
    
    const isCopier = planId.startsWith('copier-');
    const isDedi = planId.startsWith('analyzer-');
    const isAnalytics = planId.startsWith('analytics-');
    const interval = planId.split('-')[1]; // monthly, quarterly, semiannually, annual
    
    const productNameLower = overdueSubscription.productName?.toLowerCase() || '';
    const subInterval = overdueSubscription.interval?.toLowerCase() || '';
    const subIntervalCount = Number(overdueSubscription.intervalCount) || 1;
    
    if (isCopier) {
      const isCopierProduct = productNameLower.includes('copier') || productNameLower.includes('account');
      if (!isCopierProduct) return false;
      
      if (interval === 'monthly' && subInterval === 'month' && subIntervalCount === 1) return true;
      if (interval === 'quarterly' && subInterval === 'month' && subIntervalCount === 3) return true;
      if (interval === 'semiannually' && subInterval === 'month' && subIntervalCount === 6) return true;
      if (interval === 'annual' && subInterval === 'year' && subIntervalCount === 1) return true;
    } else if (isAnalytics) {
      const isAnalyticsProduct = productNameLower === 'analyzer' || productNameLower.includes('advanced analytics');
      if (!isAnalyticsProduct) return false;
      
      if (interval === 'monthly' && subInterval === 'month' && subIntervalCount === 1) return true;
      if (interval === 'quarterly' && subInterval === 'month' && subIntervalCount === 3) return true;
      if (interval === 'semiannually' && subInterval === 'month' && subIntervalCount === 6) return true;
      if (interval === 'annual' && subInterval === 'year' && subIntervalCount === 1) return true;
    } else if (isDedi) {
      const isDediProduct = productNameLower.includes('dedicated') || productNameLower.includes('environment');
      if (!isDediProduct) return false;
      
      if (interval === 'monthly' && subInterval === 'month' && subIntervalCount === 1) return true;
      if (interval === 'quarterly' && subInterval === 'month' && subIntervalCount === 3) return true;
      if (interval === 'semiannually' && subInterval === 'month' && subIntervalCount === 6) return true;
      if (interval === 'annual' && subInterval === 'year' && subIntervalCount === 1) return true;
    }
    return false;
  };

  // Plan pricing configuration
  const planPricing: Record<string, { basePrice: number; perAccount: number; firstAccountPrice?: number; additionalAccountPrice?: number; interval: string; intervalText: string; isFlat?: boolean; premiumPrice?: number }> = {
    'copier-monthly': { basePrice: 10, perAccount: 10, premiumPrice: 15, interval: 'month', intervalText: 'Monthly' },
    'copier-quarterly': { basePrice: 30, perAccount: 30, premiumPrice: 45, interval: 'quarter', intervalText: 'Quarterly' },
    'copier-semiannually': { basePrice: 60, perAccount: 60, premiumPrice: 75, interval: 'semiannual', intervalText: 'Semi-annually' },
    'copier-annual': { basePrice: 100, perAccount: 100, premiumPrice: 150, interval: 'year', intervalText: 'Annual' },
    'analyzer-monthly': { basePrice: 30, perAccount: 10, firstAccountPrice: 30, additionalAccountPrice: 10, interval: 'month', intervalText: 'Monthly' },
    'analyzer-annual': { basePrice: 300, perAccount: 100, firstAccountPrice: 300, additionalAccountPrice: 100, interval: 'year', intervalText: 'Annual' },
    // Analyzer (Advanced Analytics) - flat subscription fee
    'analytics-monthly': { basePrice: 29.99, perAccount: 0, interval: 'month', intervalText: 'Monthly', isFlat: true },
    'analytics-quarterly': { basePrice: 84.99, perAccount: 0, interval: 'quarter', intervalText: 'Quarterly', isFlat: true },
    'analytics-semiannually': { basePrice: 159.99, perAccount: 0, interval: 'semiannual', intervalText: 'Semi-annually', isFlat: true },
    'analytics-annual': { basePrice: 299.99, perAccount: 0, interval: 'year', intervalText: 'Annual', isFlat: true }
  };

  // Handlers for standard accounts
  const handleStandardIncrement = () => {
    setStandardAccountsQuantity((prev) => prev + 1);
    setAccountsQuantity((prev) => prev + 1);
  };

  const handleStandardDecrement = () => {
    const totalAfterDecrement = standardAccountsQuantity - 1 + premiumAccountsQuantity;
    // Only allow decrement if total remains >= 2 and standard > 0
    if (standardAccountsQuantity > 0 && totalAfterDecrement >= 2) {
      setStandardAccountsQuantity((prev) => prev - 1);
      setAccountsQuantity((prev) => prev - 1);
    }
  };

  // Handlers for premium accounts
  const handlePremiumIncrement = () => {
    setPremiumAccountsQuantity((prev) => prev + 1);
    setAccountsQuantity((prev) => prev + 1);
  };

  const handlePremiumDecrement = () => {
    const totalAfterDecrement = standardAccountsQuantity + premiumAccountsQuantity - 1;
    // Only allow decrement if total remains >= 2 and premium > 0
    if (premiumAccountsQuantity > 0 && totalAfterDecrement >= 2) {
      setPremiumAccountsQuantity((prev) => prev - 1);
      setAccountsQuantity((prev) => prev - 1);
    }
  };

  // Legacy handlers for non-copier plans
  const handleIncrement = () => {
    setAccountsQuantity((prev) => prev + 1);
  };

  const handleDecrement = () => {
    const minAccounts = selectedPlanType === 'copier' ? 2 : 1;
    setAccountsQuantity((prev) => (prev > minAccounts ? prev - 1 : minAccounts));
  };

  const initialValues = {
    selectedPlanId: ''
  };

  const validationSchema = Yup.object().shape({
    selectedPlanId: Yup.string().required('Plan is required')
  });

  // Calculate total cost for TC Copier with standard and premium accounts
  const calculateCopierTotalCost = (plan: typeof planPricing[string]) => {
    const standardCost = standardAccountsQuantity * plan.basePrice;
    const premiumCost = premiumAccountsQuantity * (plan.premiumPrice || plan.basePrice);
    return standardCost + premiumCost;
  };

  const getPriceName = (planId: string, isPremium: boolean = false): string => {
    const isDedi = planId.startsWith('analyzer-');
    const isAnalytics = planId.startsWith('analytics-');
    const interval = planId.split('-')[1]; // monthly, quarterly, semiannually, annual
    
    // Map to nickname format
    const intervalMapping: Record<string, string> = {
      'monthly': 'monthly',
      'quarterly': 'quarterly',
      'semiannually': 'semiannually',
      'annual': 'annually'
    };
    
    const mappedInterval = intervalMapping[interval] || interval;
    
    if (isAnalytics) {
      return `analyzer_${mappedInterval}`;
    } else if (isDedi) {
      return `dedicatedenv_${mappedInterval}`;
    } else {
      // For TC Copier, return premium or standard based on flag
      return isPremium ? `premium_${mappedInterval}` : `standard_${mappedInterval}`;
    }
  };

  // Core purchase execution logic - can be called directly or after card save
  const executePurchase = async (values: typeof initialValues, paymentMethodOverride?: string) => {
    if (!user?.tcId) {
      service.dispatchSnackbar(dispatch, 'User not authenticated', 'alert', 'error', 5000);
      return;
    }

    const paymentMethodToUse = paymentMethodOverride || selectedCard;

    // Calculate total cost to check if credit covers it
    const selectedPlan = values.selectedPlanId ? planPricing[values.selectedPlanId] : null;
    const isCopierPlan = values.selectedPlanId.startsWith('copier-');
    const isDediPlan = values.selectedPlanId.startsWith('analyzer-');
    const totalCost = selectedPlan ? (
      isCopierPlan 
        ? calculateCopierTotalCost(selectedPlan)
        : isDediPlan 
          ? (selectedPlan.firstAccountPrice || selectedPlan.basePrice) + ((selectedPlan.additionalAccountPrice || selectedPlan.perAccount) * Math.max(0, accountsQuantity - 1))
          : selectedPlan.basePrice // Flat fee for analytics plans
    ) : 0;

    // Only require payment method if credit doesn't cover the full amount
    if (paymentType === 'card' && !paymentMethodToUse && creditBalance < totalCost) {
      service.dispatchSnackbar(dispatch, 'Please select a payment method', 'alert', 'error', 5000);
      return;
    }

    setLoading(true);
    try {
      const client = await getBillingClient();
      
      // Set the selected card as default if using card payment and it's different from current default
      if (paymentType === 'card' && paymentMethodToUse) {
        const cardToSetDefault = localPaymentCards.find(c => c.paymentMethodId === paymentMethodToUse);
        if (cardToSetDefault && !cardToSetDefault.isDefault?.value) {
          console.log('Setting card as default:', cardToSetDefault.id);
          const setDefaultReq: SetDefaultPaymentMethodReq.AsObject = { userId: user.tcId, cardId: cardToSetDefault.id, paymentMethodId: '' };
          await client.setDefaultPaymentMethod(setDefaultReq);
          
          // Update local state to reflect the new default
          setLocalPaymentCards(prevCards => 
            prevCards.map(card => ({
              ...card,
              isDefault: card.id === cardToSetDefault.id ? { value: true } : { value: false }
            }))
          );
        }
      }
      
      // First, fetch prices to get the actual price IDs
      const pricesReq: ListPricesReq.AsObject = {
        userId: user.tcId
      };
      
      const pricesResult = await client.listPrices(pricesReq) as ListPricesReply.AsObject;
      console.log('Prices list:', pricesResult.pricesList);
      
      const isCopierPlan = values.selectedPlanId.startsWith('copier-');
      const isDedi = values.selectedPlanId.startsWith('analyzer-');
      const isAnalytics = values.selectedPlanId.startsWith('analytics-');
      
      // For TC Copier plans with premium accounts, we need to create separate subscriptions
      if (isCopierPlan && premiumAccountsQuantity > 0) {
        // Handle TC Copier with premium accounts - create separate subscriptions
        let totalAmount = 0;
        const subscriptionsToCreate: Array<{ priceId: string; quantity: number; nickname: string }> = [];
        
        // Add standard accounts subscription if any
        if (standardAccountsQuantity > 0) {
          const standardNickname = getPriceName(values.selectedPlanId, false);
          const standardPrice = pricesResult.pricesList?.find((p: Price.AsObject) => p.nickname === standardNickname);
          if (!standardPrice) {
            service.dispatchSnackbar(dispatch, `Price "${standardNickname}" not found. Please contact support.`, 'alert', 'error', 5000);
            setLoading(false);
            return;
          }
          subscriptionsToCreate.push({ priceId: standardPrice.id, quantity: standardAccountsQuantity, nickname: standardNickname });
          totalAmount += standardPrice.unitAmount * standardAccountsQuantity;
        }
        
        // Add premium accounts subscription
        const premiumNickname = getPriceName(values.selectedPlanId, true);
        const premiumPrice = pricesResult.pricesList?.find((p: Price.AsObject) => p.nickname === premiumNickname);
        if (!premiumPrice) {
          service.dispatchSnackbar(dispatch, `Price "${premiumNickname}" not found. Please contact support.`, 'alert', 'error', 5000);
          setLoading(false);
          return;
        }
        subscriptionsToCreate.push({ priceId: premiumPrice.id, quantity: premiumAccountsQuantity, nickname: premiumNickname });
        totalAmount += premiumPrice.unitAmount * premiumAccountsQuantity;
        
        const amountAfterCredit = Math.max(0, totalAmount - creditBalance);
        
        // Crypto path covers single- and multi-product carts uniformly: all
        // items ride one CreateSubscriptionReq.itemsList and the backend cron
        // fans them out into one Stripe subscription per item (each carrying
        // cancel_at_period_end so the missing recurring rail is harmless).
        if (paymentType === 'crypto' && amountAfterCredit > 0 && subscriptionsToCreate.length > 0) {
          const [primaryItem] = subscriptionsToCreate;
          const cryptoReq: CreateCryptoPaymentRequest.AsObject = {
            userId: user.tcId,
            createSubscription: {
              userId: user.tcId,
              priceId: primaryItem.priceId,
              quantity: primaryItem.quantity,
              toltReferral: (window as any)?.tolt_referral || '',
              isDedi: false,
              itemsList: subscriptionsToCreate.map((s) => ({
                priceId: s.priceId,
                quantity: s.quantity
              }))
            } as any,
            creditPurchaseTotal: totalAmount,
            purchaseTotal: amountAfterCredit,
            payInvoiceId: ''
          };

          const cryptoRes = await client.createCryptoPayment(cryptoReq);
          const cryptoResult = cryptoRes as CreateCryptoPaymentReply.AsObject;

          if (cryptoResult.purchaseUrl) {
            window.open(cryptoResult.purchaseUrl, '_self');
            resetDrawer();
            onClose();
          } else {
            throw new Error('Failed to get crypto payment URL');
          }
          return;
        }
        
        // Create subscriptions sequentially for card payment
        let lastClientSecret: string | null = null;
        for (const sub of subscriptionsToCreate) {
          console.log(`Creating subscription for ${sub.nickname} with quantity ${sub.quantity}`);
          const req: CreateSubscriptionReq.AsObject = {
            userId: user.tcId,
            priceId: sub.priceId,
            quantity: sub.quantity,
            toltReferral: (window as any)?.tolt_referral || '',
            isDedi: false,
            useTrial: false,
            itemsList: [],
            coupon: '',
            checkoutRef: ''
          };
          
          const result = await client.createSubscription(req) as CreateSubscriptionReply.AsObject;
          console.log('CreateSubscriptionReply:', result);
          
          if (result.clientSecret) {
            lastClientSecret = result.clientSecret;
            // Confirm payment for each subscription
            await confirmPayment(result.clientSecret, false, paymentMethodToUse);
          }
        }
        
        if (!lastClientSecret && creditBalance >= totalAmount) {
          // All subscriptions created successfully without payment
          await handleRoleUpdate(false);
        } else if (!lastClientSecret) {
          service.dispatchSnackbar(dispatch, 'Subscriptions created successfully!', 'alert', 'success', 3000);
          await handleRoleUpdate(false);
        }
        
        return;
      }
      
      // Standard flow for non-premium TC Copier, Dedicated Env, and Analytics
      const priceNickname = getPriceName(values.selectedPlanId, false);
      console.log('Looking for price with nickname:', priceNickname);
      
      // Find the price by nickname
      const price = pricesResult.pricesList?.find((p: Price.AsObject) => p.nickname === priceNickname);
      
      if (!price) {
        service.dispatchSnackbar(dispatch, `Price "${priceNickname}" not found. Please contact support.`, 'alert', 'error', 5000);
        setLoading(false);
        return;
      }
      
      console.log('Found price:', price);
      
      // Calculate amount after credit
      // Analytics plans are flat fee (quantity: 1), Copier and Dedi use accountsQuantity
      const quantity = isAnalytics ? 1 : (isCopierPlan || isDedi) ? accountsQuantity : 1;
      const totalAmount = price.unitAmount * quantity;
      const amountAfterCredit = Math.max(0, totalAmount - creditBalance);
      
      // Handle crypto payment (only if there's an amount to pay after credit)
      if (paymentType === 'crypto' && amountAfterCredit > 0) {
        const cryptoReq: CreateCryptoPaymentRequest.AsObject = {
          userId: user.tcId,
          createSubscription: {
            userId: user.tcId,
            priceId: price.id,
            quantity: quantity,
            toltReferral: (window as any)?.tolt_referral || '',
            isDedi: isDedi || isAnalytics, // Set isDedi for both Dedicated Env and Analyzer products
            useTrial: false,
            itemsList: [],
            coupon: '',
            checkoutRef: ''
          },
          creditPurchaseTotal: totalAmount,
          purchaseTotal: amountAfterCredit,
          payInvoiceId: ''
        };

        const cryptoRes = await client.createCryptoPayment(cryptoReq);
        const cryptoResult = cryptoRes as CreateCryptoPaymentReply.AsObject;
        
        if (cryptoResult.purchaseUrl) {
          window.open(cryptoResult.purchaseUrl, '_self');
          resetDrawer();
          onClose();
        } else {
          throw new Error('Failed to get crypto payment URL');
        }
        return;
      }
      
      // Handle card payment
      const req: CreateSubscriptionReq.AsObject = {
        userId: user.tcId,
        priceId: price.id, // Use the actual price ID from the database
        quantity: quantity,
        toltReferral: (window as any)?.tolt_referral || '',
        isDedi: isDedi || isAnalytics, // Set isDedi for both Dedicated Env and Analyzer products
        useTrial: false,
        itemsList: [],
        coupon: '',
        checkoutRef: ''
      };

      console.log('CreateSubscriptionReq:', req);
      const result = await client.createSubscription(req) as CreateSubscriptionReply.AsObject;
      console.log('CreateSubscriptionReply: ', result);
      
      if (result.clientSecret) {
        // Payment confirmation is needed
        await confirmPayment(result.clientSecret, isDedi || isAnalytics, paymentMethodToUse);
      } else if (creditBalance >= totalAmount) {
        // Subscription created successfully without payment (covered by credit balance)
        await handleRoleUpdate(isDedi || isAnalytics);
      } else {
        // No clientSecret but credit doesn't cover - this is unexpected
        console.error('Unexpected: No clientSecret returned but credit balance insufficient');
        service.dispatchSnackbar(dispatch, 'Failed to process subscription. Please contact support.', 'alert', 'error', 5000);
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Error creating subscription:', err);
      const message = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
      service.dispatchSnackbar(dispatch, message || 'Failed to create subscription', 'alert', 'error', 5000);
      setLoading(false);
    }
  };

  const handleSubmit = async (values: typeof initialValues) => {
    // If user is adding a new card with valid details, save card first then purchase
    if (showAddCard && isCardComplete && paymentType === 'card') {
      // Store form values and trigger card save with pending purchase
      formValuesRef.current = values;
      setPendingPurchase(true);
      setConfirmSaveCard(true);
      setTimeout(() => setConfirmSaveCard(false), 100);
      return;
    }

    // Otherwise, proceed with normal purchase
    await executePurchase(values);
  };

  const confirmPayment = async (clientSecret: string, isDedi: boolean, paymentMethod: string) => {
    try {
      const stripeInstance = await getUserStripe(user?.tcId || '', getBillingClient);
      if (!stripeInstance) {
        throw new Error('Stripe failed to load');
      }

      const result = await stripeInstance.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod,
          return_url: `${config.hostname}/account-settings/subscriptions`
        },
        {}
      );

      console.log('Stripe confirmCardPayment result:', result);

      if (result?.error) {
        console.error('Payment error:', result.error);
        service.dispatchSnackbar(dispatch, getStripeErrorMessage(result?.error?.code) || result?.error?.message || 'Payment failed', 'alert', 'error', 5000);
        setLoading(false);
        return; // Don't close drawer - allow user to try another card
      }
      
      // Check paymentIntent status for additional failure cases
      const paymentIntent = result?.paymentIntent;
      if (!paymentIntent) {
        console.error('No payment intent returned');
        service.dispatchSnackbar(dispatch, 'Payment failed. Please try again.', 'alert', 'error', 5000);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'requires_payment_method') {
        // Card was declined
        console.error('Payment declined - requires_payment_method');
        service.dispatchSnackbar(dispatch, 'Payment declined. Please try another card.', 'alert', 'error', 5000);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'requires_action') {
        // 3D Secure or other action required - Stripe should handle this automatically
        console.log('Payment requires action');
        service.dispatchSnackbar(dispatch, 'Payment requires additional verification.', 'alert', 'warning', 5000);
        setLoading(false);
        return;
      }

      if (paymentIntent.status !== 'succeeded') {
        // Any other non-success status
        console.error('Payment not successful, status:', paymentIntent.status);
        service.dispatchSnackbar(dispatch, `Payment not completed. Status: ${paymentIntent.status}`, 'alert', 'error', 5000);
        setLoading(false);
        return;
      }

      // Payment succeeded
      trackPurchase({
        transaction_id: paymentIntent.id || '',
        value: (paymentIntent.amount ?? 0) / 100,
        currency: paymentIntent.currency || 'usd',
        checkout_entry: 'upgrade_drawer'
      });
      service.dispatchSnackbar(dispatch, 'Payment successful. Please wait while we upgrade your subscription', 'alert', 'success', 5000);
      await handleRoleUpdate(isDedi);
    } catch (err: any) {
      console.error('Payment confirmation error:', err);
      service.dispatchSnackbar(dispatch, err.message || 'Payment confirmation failed', 'alert', 'error', 5000);
      setLoading(false);
    }
  };

  const handleRoleUpdate = async (isDedi: boolean) => {
    // Wait for backend to process the subscription
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    service.dispatchSnackbar(dispatch, 'Subscription successfully created!', 'alert', 'success', 5000);
    
    if (onSuccess) {
      await onSuccess();
    }
    
    resetDrawer();
    onClose();
  };

  return (
    <>
      {/* Floating tooltip that follows the mouse */}
      {tooltipPos.visible && (
        <Box
          sx={{
            position: 'fixed',
            right: `calc(100vw - ${tooltipPos.x}px + 12px)`,
            top: tooltipPos.y - 40,
            backgroundColor: '#1a1a1a',
            color: '#fff',
            padding: '8px 14px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            zIndex: 99999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            pointerEvents: 'none'
          }}
        >
          Copier subscription required to purchase Dedicated Environment
        </Box>
      )}
      <Drawer
      anchor="right"
      open={isOpen}
      onClose={(event, reason) => {
        // Prevent closing during purchase
        if (loading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        onClose();
      }}
      keepMounted
      PaperProps={{
        sx: {
          boxShadow: 'none',
          backgroundImage: 'none'
        }
      }}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1000 }}
    >
      <Box
        sx={{
          width: {
            xs: '100%',
            sm: '500px',
            md: '540px',
            lg: '580px',
            xl: '620px'
          },
          height: '100%',
          maxHeight: '100vh',
          borderTopLeftRadius: '40px',
          borderBottomLeftRadius: '40px',
          backgroundColor: (theme) => theme.palette.background.paper,
          overflow: 'hidden'
        }}
        display="flex"
        flexDirection="column"
      >
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, handleChange, handleSubmit, setFieldValue }) => {
            const selectedPlan = values.selectedPlanId ? planPricing[values.selectedPlanId] : null;
            const isCopierPlan = values.selectedPlanId.startsWith('copier-');
            const isDediPlan = values.selectedPlanId.startsWith('analyzer-');
            const isAnalyticsPlan = values.selectedPlanId.startsWith('analytics-');
            // Calculate total cost: TC Copier = (standard * basePrice) + (premium * premiumPrice), Dedicated Env = firstAccountPrice + (additionalAccountPrice * (quantity - 1)), Analyzer = flat fee
            const totalCost = selectedPlan ? (
              isCopierPlan 
                ? calculateCopierTotalCost(selectedPlan)
                : isDediPlan 
                  ? (selectedPlan.firstAccountPrice || selectedPlan.basePrice) + ((selectedPlan.additionalAccountPrice || selectedPlan.perAccount) * Math.max(0, accountsQuantity - 1))
                  : selectedPlan.basePrice // Flat fee for analytics plans
            ) : 0;
            const planName = values.selectedPlanId.startsWith('copier-') 
              ? 'Copier' 
              : values.selectedPlanId.startsWith('analytics-') 
                ? 'Analyzer' 
                : 'Dedicated Environment';
            const copierRequired = !hasCopierSubscription();
            
            // Debug log
            console.log('copierRequired:', copierRequired, 'hasCopierSubscription:', hasCopierSubscription());

            return (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                {/* 1. Header Section */}
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  gap={1}
                  sx={{
                    paddingTop: { xl: 4.5, lg: 4, md: 3.5, sm: 3, xs: 2.5 },
                    paddingRight: { xl: 4.5, lg: 4, md: 3.5, sm: 3, xs: 2.5 },
                    paddingLeft: { xl: 4.5, lg: 4, md: 3.5, sm: 3, xs: 2.5 }
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontSize={{ lg: 32, md: 28, sm: 24, xs: 20 }} fontWeight={700} lineHeight="100%">
                      {currentStep === 'plan' ? 'Purchase Subscription' : currentStep === 'configure' ? 'Configure Purchase' : 'Payment Method'}
                    </Typography>
                  <IconButton onClick={onClose} size="large">
              <CloseOutlined width={30} height={30} />
            </IconButton>
                  </Box>
                  <Typography variant="body2" color="textPrimary" fontSize={textSizeByBreakpoint(14)} fontWeight={300} lineHeight="100%" marginTop={0.5}>
                    {currentStep === 'plan' && 'Choose the subscription plan that works best for you'}
                    {currentStep === 'configure' && 'Select the number of accounts and review pricing'}
                    {currentStep === 'payment' && 'Choose your payment method to complete purchase'}

                  </Typography>
                </Box>

                <Box
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  sx={{
                    height: '100%',
                    overflowY: 'auto',
                    padding: { xl: 4.5, lg: 4, md: 3.5, sm: 3, xs: 2.5 },
                    paddingTop: 3
                  }}
                >
                  {/* Step 1: Plan Selection */}
                  {currentStep === 'plan' && (
                  <>
                  {/* Subscription Cards */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} mb={2} fontSize={textSizeByBreakpoint(18)}>
                      Copier
                    </Typography>
                    <Stack spacing={1.5}>
                      {/* Monthly */}
                      {hasSubscription('copier-monthly') ? (
                        <Box
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '12px',
                            p: 2,
                            backgroundColor: alpha(theme.palette.text.secondary, 0.05)
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ opacity: 0.6 }}>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                                Monthly - Already subscribed
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Manage your existing subscription
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onManageSubscription?.();
                              }}
                              sx={{ 
                                fontSize: textSizeByBreakpoint(14), 
                                whiteSpace: 'nowrap',
                                backgroundColor: theme.palette.primary.main,
                                ':hover': {
                                  backgroundColor: theme.palette.primary[400]
                                }
                              }}
                            >
                              Manage
                            </Button>
                          </Box>
                        </Box>
                      ) : isOverduePlan('copier-monthly') ? (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'copier-monthly')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'copier-monthly' ? theme.palette.error.main : theme.palette.error.light}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'copier-monthly' ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.error.main, 0.02),
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.05)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'copier-monthly'}
                              onChange={() => setFieldValue('selectedPlanId', 'copier-monthly')}
                              value="copier-monthly"
                              sx={{
                                color: theme.palette.error.main,
                                '&.Mui-checked': {
                                  color: theme.palette.error.main
                                }
                              }}
                            />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                  Monthly
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: theme.palette.error.main, 
                                    fontWeight: 600,
                                    fontSize: textSizeByBreakpoint(12),
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px'
                                  }}
                                >
                                  Overdue
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed monthly • Payment required
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)} color="error">
                            $20.00
                          </Typography>
                        </Box>
                      </Box>
                      ) : (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'copier-monthly')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'copier-monthly' ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'copier-monthly' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'copier-monthly'}
                              onChange={() => setFieldValue('selectedPlanId', 'copier-monthly')}
                              value="copier-monthly"
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Monthly
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed monthly • 2 accounts minimum
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $20.00
                          </Typography>
                        </Box>
                      </Box>
                      )}

                      {/* Quarterly */}
                      {hasSubscription('copier-quarterly') ? (
                        <Box
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '12px',
                            p: 2,
                            backgroundColor: alpha(theme.palette.text.secondary, 0.05)
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ opacity: 0.6 }}>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                                Quarterly - Already subscribed
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Manage your existing subscription
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onManageSubscription?.();
                              }}
                              sx={{ 
                                fontSize: textSizeByBreakpoint(14), 
                                whiteSpace: 'nowrap',
                                backgroundColor: theme.palette.primary.main,
                                ':hover': {
                                  backgroundColor: theme.palette.primary[400]
                                }
                              }}
                            >
                              Manage
                            </Button>
                          </Box>
                        </Box>
                      ) : isOverduePlan('copier-quarterly') ? (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'copier-quarterly')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'copier-quarterly' ? theme.palette.error.main : theme.palette.error.light}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'copier-quarterly' ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.error.main, 0.02),
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.05)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'copier-quarterly'}
                              onChange={() => setFieldValue('selectedPlanId', 'copier-quarterly')}
                              value="copier-quarterly"
                              sx={{
                                color: theme.palette.error.main,
                                '&.Mui-checked': {
                                  color: theme.palette.error.main
                                }
                              }}
                            />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                  Quarterly
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: theme.palette.error.main, 
                                    fontWeight: 600,
                                    fontSize: textSizeByBreakpoint(12),
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px'
                                  }}
                                >
                                  Overdue
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed every 3 months • Payment required
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)} color="error">
                            $60.00
                          </Typography>
                        </Box>
                      </Box>
                      ) : (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'copier-quarterly')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'copier-quarterly' ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'copier-quarterly' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'copier-quarterly'}
                              onChange={() => setFieldValue('selectedPlanId', 'copier-quarterly')}
                              value="copier-quarterly"
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Quarterly
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed every 3 months • 2 accounts minimum
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $60.00
                          </Typography>
                        </Box>
                      </Box>
                      )}

                      {/* Semi-annually */}
                      {hasSubscription('copier-semiannually') ? (
                        <Box
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '12px',
                            p: 2,
                            backgroundColor: alpha(theme.palette.text.secondary, 0.05)
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ opacity: 0.6 }}>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                                Semi-annually - Already subscribed
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Manage your existing subscription
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onManageSubscription?.();
                              }}
                              sx={{ 
                                fontSize: textSizeByBreakpoint(14), 
                                whiteSpace: 'nowrap',
                                backgroundColor: theme.palette.primary.main,
                                ':hover': {
                                  backgroundColor: theme.palette.primary[400]
                                }
                              }}
                            >
                              Manage
                            </Button>
                          </Box>
                        </Box>
                      ) : isOverduePlan('copier-semiannually') ? (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'copier-semiannually')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'copier-semiannually' ? theme.palette.error.main : theme.palette.error.light}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'copier-semiannually' ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.error.main, 0.02),
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.05)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'copier-semiannually'}
                              onChange={() => setFieldValue('selectedPlanId', 'copier-semiannually')}
                              value="copier-semiannually"
                              sx={{
                                color: theme.palette.error.main,
                                '&.Mui-checked': {
                                  color: theme.palette.error.main
                                }
                              }}
                            />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                  Semi-annually
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: theme.palette.error.main, 
                                    fontWeight: 600,
                                    fontSize: textSizeByBreakpoint(12),
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px'
                                  }}
                                >
                                  Overdue
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed every 6 months • Payment required
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)} color="error">
                            $120.00
                          </Typography>
                        </Box>
                      </Box>
                      ) : (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'copier-semiannually')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'copier-semiannually' ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'copier-semiannually' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'copier-semiannually'}
                              onChange={() => setFieldValue('selectedPlanId', 'copier-semiannually')}
                              value="copier-semiannually"
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Semi-annually
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed every 6 months • 2 accounts minimum
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $120.00
                          </Typography>
                        </Box>
                      </Box>
                      )}

                      {/* Annual */}
                      {hasSubscription('copier-annual') ? (
                        <Box
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '12px',
                            p: 2,
                            backgroundColor: alpha(theme.palette.text.secondary, 0.05)
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ opacity: 0.6 }}>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                                Annual - Already subscribed
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Manage your existing subscription
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onManageSubscription?.();
                              }}
                              sx={{ 
                                fontSize: textSizeByBreakpoint(14), 
                                whiteSpace: 'nowrap',
                                backgroundColor: theme.palette.primary.main,
                                ':hover': {
                                  backgroundColor: theme.palette.primary[400]
                                }
                              }}
                            >
                              Manage
                            </Button>
                          </Box>
                        </Box>
                      ) : isOverduePlan('copier-annual') ? (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'copier-annual')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'copier-annual' ? theme.palette.error.main : theme.palette.error.light}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'copier-annual' ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.error.main, 0.02),
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.05)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'copier-annual'}
                              onChange={() => setFieldValue('selectedPlanId', 'copier-annual')}
                              value="copier-annual"
                              sx={{
                                color: theme.palette.error.main,
                                '&.Mui-checked': {
                                  color: theme.palette.error.main
                                }
                              }}
                            />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                  Annual
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: theme.palette.error.main, 
                                    fontWeight: 600,
                                    fontSize: textSizeByBreakpoint(12),
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px'
                                  }}
                                >
                                  Overdue
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed annually • Payment required
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)} color="error">
                            $200.00
                          </Typography>
                        </Box>
                      </Box>
                      ) : (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'copier-annual')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'copier-annual' ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'copier-annual' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'copier-annual'}
                              onChange={() => setFieldValue('selectedPlanId', 'copier-annual')}
                              value="copier-annual"
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Annual
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed annually • 2 accounts minimum
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $200.00
                          </Typography>
                        </Box>
                      </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Dedicated Environment Cards */}
                  <Box mt={3}>
                    <Typography variant="subtitle1" fontWeight={700} mb={2} fontSize={textSizeByBreakpoint(18)}>
                      Dedicated Environment
                    </Typography>
                    <Stack spacing={1.5}>
                      {/* Purchase Options - Always show if user has copier subscription */}
                      {/* Monthly Purchase Option */}
                      {copierRequired ? (
                      <Box
                        onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY, visible: true })}
                        onMouseLeave={() => setTooltipPos((prev) => ({ ...prev, visible: false }))}
                        sx={{
                          position: 'relative',
                          border: `2px solid ${theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'not-allowed',
                          transition: 'all 0.2s',
                          backgroundColor: 'transparent',
                          opacity: 0.5
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={false}
                              value="analyzer-monthly"
                              disabled={true}
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Monthly
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed monthly
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $30.00
                          </Typography>
                        </Box>
                      </Box>
                      ) : (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'analyzer-monthly')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'analyzer-monthly' ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'analyzer-monthly' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'analyzer-monthly'}
                              onChange={() => setFieldValue('selectedPlanId', 'analyzer-monthly')}
                              value="analyzer-monthly"
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Monthly
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed monthly
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $30.00
                          </Typography>
                        </Box>
                      </Box>
                      )}

                      {/* Annual Purchase Option */}
                      {copierRequired ? (
                      <Box
                        onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY, visible: true })}
                        onMouseLeave={() => setTooltipPos((prev) => ({ ...prev, visible: false }))}
                        sx={{
                          position: 'relative',
                          border: `2px solid ${theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'not-allowed',
                          transition: 'all 0.2s',
                          backgroundColor: 'transparent',
                          opacity: 0.5
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={false}
                              value="analyzer-annual"
                              disabled={true}
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Annual
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed annually
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $300.00
                          </Typography>
                        </Box>
                      </Box>
                      ) : (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'analyzer-annual')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'analyzer-annual' ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'analyzer-annual' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'analyzer-annual'}
                              onChange={() => setFieldValue('selectedPlanId', 'analyzer-annual')}
                              value="analyzer-annual"
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Annual
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed annually
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $300.00
                          </Typography>
                        </Box>
                      </Box>
                      )}

                      {/* Existing Subscriptions - Show greyed out boxes for management */}
                      {(hasSubscription('analyzer-monthly') || hasSubscription('analyzer-annual')) && (
                        <Box mt={1}>
                          <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(12)} sx={{ mb: 1, display: 'block' }}>
                            Your existing subscriptions
                          </Typography>
                          <Stack spacing={1}>
                            {hasSubscription('analyzer-monthly') && (
                              <Box
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: '12px',
                                  p: 2,
                                  backgroundColor: alpha(theme.palette.text.secondary, 0.05)
                                }}
                              >
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                  <Box sx={{ opacity: 0.6 }}>
                                    <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                                      Monthly - Already subscribed
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                      Manage your existing subscription
                                    </Typography>
                                  </Box>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onManageSubscription?.();
                                    }}
                                    sx={{ 
                                      fontSize: textSizeByBreakpoint(14), 
                                      whiteSpace: 'nowrap',
                                      backgroundColor: theme.palette.primary.main,
                                      ':hover': {
                                        backgroundColor: theme.palette.primary[400]
                                      }
                                    }}
                                  >
                                    Manage
                                  </Button>
                                </Box>
                              </Box>
                            )}
                            {hasSubscription('analyzer-annual') && (
                              <Box
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: '12px',
                                  p: 2,
                                  backgroundColor: alpha(theme.palette.text.secondary, 0.05)
                                }}
                              >
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                  <Box sx={{ opacity: 0.6 }}>
                                    <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                                      Annual - Already subscribed
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                      Manage your existing subscription
                                    </Typography>
                                  </Box>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onManageSubscription?.();
                                    }}
                                    sx={{ 
                                      fontSize: textSizeByBreakpoint(14), 
                                      whiteSpace: 'nowrap',
                                      backgroundColor: theme.palette.primary.main,
                                      ':hover': {
                                        backgroundColor: theme.palette.primary[400]
                                      }
                                    }}
                                  >
                                    Manage
                                  </Button>
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Analyzer (Advanced Analytics) Cards */}
                  {SHOW_ANALYZER_PURCHASE && (
                  <Box mt={3}>
                    <Typography variant="subtitle1" fontWeight={700} mb={2} fontSize={textSizeByBreakpoint(18)}>
                      Analyzer
                    </Typography>
                    <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)} mb={2}>
                      Advanced analytics and insights for your trading performance
                    </Typography>
                    
                    {/* Show single "Already Subscribed" box if user has any Analyzer subscription */}
                    {hasAnyAnalyzerSubscription() ? (
                      <Box
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          backgroundColor: alpha(theme.palette.success.main, 0.05)
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box sx={{ opacity: 0.8 }}>
                            <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                              Analyzer - Already subscribed
                            </Typography>
                            <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                              You already have an active Analyzer subscription. Manage your existing subscription.
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onManageSubscription?.();
                            }}
                            sx={{ 
                              fontSize: textSizeByBreakpoint(14), 
                              whiteSpace: 'nowrap',
                              backgroundColor: theme.palette.primary.main,
                              ':hover': {
                                backgroundColor: theme.palette.primary[400]
                              }
                            }}
                          >
                            Manage
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                    <Stack spacing={1.5}>
                      {/* Monthly */}
                      {hasSubscription('analytics-monthly') ? (
                        <Box
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '12px',
                            p: 2,
                            backgroundColor: alpha(theme.palette.text.secondary, 0.05)
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ opacity: 0.6 }}>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                                Monthly - Already subscribed
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Manage your existing subscription
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onManageSubscription?.();
                              }}
                              sx={{ 
                                fontSize: textSizeByBreakpoint(14), 
                                whiteSpace: 'nowrap',
                                backgroundColor: theme.palette.primary.main,
                                ':hover': {
                                  backgroundColor: theme.palette.primary[400]
                                }
                              }}
                            >
                              Manage
                            </Button>
                          </Box>
                        </Box>
                      ) : isOverduePlan('analytics-monthly') ? (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'analytics-monthly')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'analytics-monthly' ? theme.palette.error.main : theme.palette.error.light}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'analytics-monthly' ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.error.main, 0.02),
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.05)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'analytics-monthly'}
                              onChange={() => setFieldValue('selectedPlanId', 'analytics-monthly')}
                              value="analytics-monthly"
                              sx={{
                                color: theme.palette.error.main,
                                '&.Mui-checked': {
                                  color: theme.palette.error.main
                                }
                              }}
                            />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                  Monthly
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: theme.palette.error.main, 
                                    fontWeight: 600,
                                    fontSize: textSizeByBreakpoint(12),
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px'
                                  }}
                                >
                                  Overdue
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed monthly • Payment required
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)} color="error">
                            $29.99
                          </Typography>
                        </Box>
                      </Box>
                      ) : (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'analytics-monthly')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'analytics-monthly' ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'analytics-monthly' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'analytics-monthly'}
                              onChange={() => setFieldValue('selectedPlanId', 'analytics-monthly')}
                              value="analytics-monthly"
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Monthly
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed monthly
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $29.99
                          </Typography>
                        </Box>
                      </Box>
                      )}

                      {/* Quarterly */}
                      {hasSubscription('analytics-quarterly') ? (
                        <Box
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '12px',
                            p: 2,
                            backgroundColor: alpha(theme.palette.text.secondary, 0.05)
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ opacity: 0.6 }}>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                                Quarterly - Already subscribed
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Manage your existing subscription
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onManageSubscription?.();
                              }}
                              sx={{ 
                                fontSize: textSizeByBreakpoint(14), 
                                whiteSpace: 'nowrap',
                                backgroundColor: theme.palette.primary.main,
                                ':hover': {
                                  backgroundColor: theme.palette.primary[400]
                                }
                              }}
                            >
                              Manage
                            </Button>
                          </Box>
                        </Box>
                      ) : isOverduePlan('analytics-quarterly') ? (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'analytics-quarterly')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'analytics-quarterly' ? theme.palette.error.main : theme.palette.error.light}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'analytics-quarterly' ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.error.main, 0.02),
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.05)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'analytics-quarterly'}
                              onChange={() => setFieldValue('selectedPlanId', 'analytics-quarterly')}
                              value="analytics-quarterly"
                              sx={{
                                color: theme.palette.error.main,
                                '&.Mui-checked': {
                                  color: theme.palette.error.main
                                }
                              }}
                            />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                  Quarterly
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: theme.palette.error.main, 
                                    fontWeight: 600,
                                    fontSize: textSizeByBreakpoint(12),
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px'
                                  }}
                                >
                                  Overdue
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed every 3 months • Payment required
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)} color="error">
                            $84.99
                          </Typography>
                        </Box>
                      </Box>
                      ) : (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'analytics-quarterly')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'analytics-quarterly' ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'analytics-quarterly' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'analytics-quarterly'}
                              onChange={() => setFieldValue('selectedPlanId', 'analytics-quarterly')}
                              value="analytics-quarterly"
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Quarterly
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed every 3 months • Save 6%
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $84.99
                          </Typography>
                        </Box>
                      </Box>
                      )}

                      {/* Semi-annually */}
                      {hasSubscription('analytics-semiannually') ? (
                        <Box
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '12px',
                            p: 2,
                            backgroundColor: alpha(theme.palette.text.secondary, 0.05)
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ opacity: 0.6 }}>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                                Semi-annually - Already subscribed
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Manage your existing subscription
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onManageSubscription?.();
                              }}
                              sx={{ 
                                fontSize: textSizeByBreakpoint(14), 
                                whiteSpace: 'nowrap',
                                backgroundColor: theme.palette.primary.main,
                                ':hover': {
                                  backgroundColor: theme.palette.primary[400]
                                }
                              }}
                            >
                              Manage
                            </Button>
                          </Box>
                        </Box>
                      ) : isOverduePlan('analytics-semiannually') ? (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'analytics-semiannually')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'analytics-semiannually' ? theme.palette.error.main : theme.palette.error.light}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'analytics-semiannually' ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.error.main, 0.02),
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.05)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'analytics-semiannually'}
                              onChange={() => setFieldValue('selectedPlanId', 'analytics-semiannually')}
                              value="analytics-semiannually"
                              sx={{
                                color: theme.palette.error.main,
                                '&.Mui-checked': {
                                  color: theme.palette.error.main
                                }
                              }}
                            />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                  Semi-annually
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: theme.palette.error.main, 
                                    fontWeight: 600,
                                    fontSize: textSizeByBreakpoint(12),
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px'
                                  }}
                                >
                                  Overdue
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed every 6 months • Payment required
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)} color="error">
                            $159.99
                          </Typography>
                        </Box>
                      </Box>
                      ) : (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'analytics-semiannually')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'analytics-semiannually' ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'analytics-semiannually' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'analytics-semiannually'}
                              onChange={() => setFieldValue('selectedPlanId', 'analytics-semiannually')}
                              value="analytics-semiannually"
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Semi-annually
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed every 6 months • Save 11%
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $159.99
                          </Typography>
                        </Box>
                      </Box>
                      )}

                      {/* Annual */}
                      {hasSubscription('analytics-annual') ? (
                        <Box
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '12px',
                            p: 2,
                            backgroundColor: alpha(theme.palette.text.secondary, 0.05)
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ opacity: 0.6 }}>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)} color="textSecondary">
                                Annual - Already subscribed
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Manage your existing subscription
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onManageSubscription?.();
                              }}
                              sx={{ 
                                fontSize: textSizeByBreakpoint(14), 
                                whiteSpace: 'nowrap',
                                backgroundColor: theme.palette.primary.main,
                                ':hover': {
                                  backgroundColor: theme.palette.primary[400]
                                }
                              }}
                            >
                              Manage
                            </Button>
                          </Box>
                        </Box>
                      ) : isOverduePlan('analytics-annual') ? (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'analytics-annual')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'analytics-annual' ? theme.palette.error.main : theme.palette.error.light}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'analytics-annual' ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.error.main, 0.02),
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.05)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'analytics-annual'}
                              onChange={() => setFieldValue('selectedPlanId', 'analytics-annual')}
                              value="analytics-annual"
                              sx={{
                                color: theme.palette.error.main,
                                '&.Mui-checked': {
                                  color: theme.palette.error.main
                                }
                              }}
                            />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                  Annual
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: theme.palette.error.main, 
                                    fontWeight: 600,
                                    fontSize: textSizeByBreakpoint(12),
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px'
                                  }}
                                >
                                  Overdue
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed annually • Payment required
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)} color="error">
                            $299.99
                          </Typography>
                        </Box>
                      </Box>
                      ) : (
                      <Box
                        onClick={() => setFieldValue('selectedPlanId', 'analytics-annual')}
                        sx={{
                          border: `2px solid ${values.selectedPlanId === 'analytics-annual' ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: '12px',
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: values.selectedPlanId === 'analytics-annual' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={values.selectedPlanId === 'analytics-annual'}
                              onChange={() => setFieldValue('selectedPlanId', 'analytics-annual')}
                              value="analytics-annual"
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                Annual
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Billed annually • Save 17%
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            $299.99
                          </Typography>
                        </Box>
                      </Box>
                      )}
                    </Stack>
                    )}
                  </Box>
                  )}
                  </>
                  )}

                  {/* Step 2: Configure Purchase */}
                  {currentStep === 'configure' && selectedPlan && (
                  <>
                    {/* Selected Plan Summary */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={500} mb={1} fontSize={textSizeByBreakpoint(14)} color="textSecondary">
                        Selected Plan
                      </Typography>
                      <Box
                        sx={{
                          border: `2px solid ${theme.palette.primary.main}`,
                          borderRadius: '12px',
                          p: 2,
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Radio
                              checked={true}
                              value={values.selectedPlanId}
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                {planName} - {selectedPlan.intervalText}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                {isDediPlan 
                                  ? `$${(selectedPlan.firstAccountPrice || selectedPlan.basePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} first, $${(selectedPlan.additionalAccountPrice || selectedPlan.perAccount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} each additional`
                                  : isAnalyticsPlan
                                    ? `Flat fee - $${selectedPlan.basePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per ${selectedPlan.interval}`
                                    : `$${selectedPlan.basePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per ${selectedPlan.interval}${isCopierPlan ? ' • 2 accounts minimum' : ''}`
                                }
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(18)}>
                            ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Account Quantity Selector (for TC Copier and Dedicated Environment only - not for Analyzer flat fee) */}
                    {(isCopierPlan || isDediPlan) && (
                      <Box mt={2}>
                        <Typography variant="subtitle1" fontWeight={700} mb={1} fontSize={textSizeByBreakpoint(18)}>
                          Number of Accounts
                        </Typography>
                        <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(16)} mb={2} fontWeight={500}>
                          {isCopierPlan 
                            ? 'Copier requires a minimum of 2 accounts total. Mix Standard and Premium accounts as needed.'
                            : 'Dedicated Environment requires a minimum of 1 account. Add more accounts as needed.'
                          }
                        </Typography>

                        {/* Counter Controls for Copier - Standard and Premium */}
                        {isCopierPlan ? (
                          <Stack spacing={2} mb={3}>
                            {/* Standard Accounts */}
                            <Box
                              sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '12px',
                                p: 2
                              }}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                    Standard Accounts
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(12)}>
                                    ${selectedPlan.basePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{selectedPlan.interval === 'month' ? 'mo' : selectedPlan.interval === 'quarter' ? 'qtr' : selectedPlan.interval === 'semiannual' ? 'semiann' : 'yr'} per account
                                  </Typography>
                                </Box>
                                <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)} color="primary">
                                  ${(standardAccountsQuantity * selectedPlan.basePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: '8px',
                                  p: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <Tooltip 
                                  title="Minimum of 2 accounts required" 
                                  disableHoverListener={!(standardAccountsQuantity <= 0 || (standardAccountsQuantity + premiumAccountsQuantity) <= 2)}
                                  arrow
                                  PopperProps={{ sx: { zIndex: 99999 } }}
                                >
                                  <span>
                                    <IconButton 
                                      onClick={handleStandardDecrement} 
                                      size="small" 
                                      disabled={standardAccountsQuantity <= 0 || (standardAccountsQuantity + premiumAccountsQuantity) <= 2}
                                      className="notranslate"
                                      translate="no"
                                    >
                                      <MinusOutlined />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Typography fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                  {standardAccountsQuantity}
                                </Typography>
                                <IconButton onClick={handleStandardIncrement} size="small" className="notranslate" translate="no">
                                  <PlusOutlined />
                                </IconButton>
                              </Box>
                            </Box>

                            {/* Premium Accounts */}
                            <Box
                              sx={{
                                border: `1px solid ${theme.palette.warning.main}`,
                                borderRadius: '12px',
                                p: 2,
                                backgroundColor: alpha(theme.palette.warning.main, 0.05)
                              }}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Box>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="subtitle2" fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                      Premium Accounts
                                    </Typography>
                                    <Chip 
                                      label="Premium" 
                                      size="small" 
                                      sx={{ 
                                        backgroundColor: theme.palette.warning.main,
                                        color: theme.palette.warning.contrastText,
                                        fontWeight: 600,
                                        fontSize: '10px',
                                        height: '20px'
                                      }} 
                                    />
                                  </Box>
                                  <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(12)}>
                                    ${(selectedPlan.premiumPrice || selectedPlan.basePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{selectedPlan.interval === 'month' ? 'mo' : selectedPlan.interval === 'quarter' ? 'qtr' : selectedPlan.interval === 'semiannual' ? 'semiann' : 'yr'} per account (Master or Slave)
                                  </Typography>
                                </Box>
                                <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)} color="warning.main">
                                  ${(premiumAccountsQuantity * (selectedPlan.premiumPrice || selectedPlan.basePrice)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: '8px',
                                  p: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  backgroundColor: theme.palette.background.paper
                                }}
                              >
                                <Tooltip 
                                  title="Minimum of 2 accounts required" 
                                  disableHoverListener={!(premiumAccountsQuantity <= 0 || (standardAccountsQuantity + premiumAccountsQuantity) <= 2)}
                                  arrow
                                  PopperProps={{ sx: { zIndex: 99999 } }}
                                >
                                  <span>
                                    <IconButton 
                                      onClick={handlePremiumDecrement} 
                                      size="small" 
                                      disabled={premiumAccountsQuantity <= 0 || (standardAccountsQuantity + premiumAccountsQuantity) <= 2}
                                      className="notranslate"
                                      translate="no"
                                    >
                                      <MinusOutlined />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Typography fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                                  {premiumAccountsQuantity}
                                </Typography>
                                <IconButton onClick={handlePremiumIncrement} size="small" className="notranslate" translate="no">
                                  <PlusOutlined />
                                </IconButton>
                              </Box>
                            </Box>

                            {/* Total Accounts Info */}
                            {(standardAccountsQuantity + premiumAccountsQuantity) < 2 && (
                              <Typography variant="caption" color="error" fontSize={textSizeByBreakpoint(12)}>
                                Minimum 2 accounts required. Please add more accounts.
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          /* Counter Control for Dedicated Environment */
                          <Box
                            sx={{
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: '8px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 3
                            }}
                          >
                            <IconButton onClick={handleDecrement} size="small" disabled={accountsQuantity <= 1} className="notranslate" translate="no">
                              <MinusOutlined />
                            </IconButton>
                            <Typography fontWeight={600} fontSize={textSizeByBreakpoint(16)}>
                              {accountsQuantity} <span style={{ fontWeight: 600, color: theme.palette.text.secondary }}>Accounts</span>
                            </Typography>
                            <IconButton onClick={handleIncrement} size="small" className="notranslate" translate="no">
                              <PlusOutlined />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Pricing Summary */}
                    <Stack spacing={1.5}>
                      {(isCopierPlan || isDediPlan) && (
                        <>
                          {isDediPlan && (
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                First account
                              </Typography>
                              <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                                ${(selectedPlan.firstAccountPrice || selectedPlan.basePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{selectedPlan.interval === 'month' ? 'mo' : 'yr'}
                              </Typography>
                            </Box>
                          )}
                          {isCopierPlan && standardAccountsQuantity > 0 && (
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Standard accounts ({standardAccountsQuantity} × ${selectedPlan.basePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                              </Typography>
                              <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                                ${(standardAccountsQuantity * selectedPlan.basePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{selectedPlan.interval === 'month' ? 'mo' : selectedPlan.interval === 'quarter' ? 'qtr' : selectedPlan.interval === 'semiannual' ? 'semiann' : 'yr'}
                              </Typography>
                            </Box>
                          )}
                          {isCopierPlan && premiumAccountsQuantity > 0 && (
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Premium accounts ({premiumAccountsQuantity} × ${(selectedPlan.premiumPrice || selectedPlan.basePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                              </Typography>
                              <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)} color="warning.main">
                                ${(premiumAccountsQuantity * (selectedPlan.premiumPrice || selectedPlan.basePrice)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{selectedPlan.interval === 'month' ? 'mo' : selectedPlan.interval === 'quarter' ? 'qtr' : selectedPlan.interval === 'semiannual' ? 'semiann' : 'yr'}
                              </Typography>
                            </Box>
                          )}
                          {isDediPlan && accountsQuantity > 1 && (
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Additional accounts ({accountsQuantity - 1} × ${(selectedPlan.additionalAccountPrice || selectedPlan.perAccount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                              </Typography>
                              <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                                ${((accountsQuantity - 1) * (selectedPlan.additionalAccountPrice || selectedPlan.perAccount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{selectedPlan.interval === 'month' ? 'mo' : 'yr'}
                              </Typography>
                            </Box>
                          )}
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                              Total accounts
                            </Typography>
                            <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                              {isCopierPlan ? standardAccountsQuantity + premiumAccountsQuantity : accountsQuantity} Accounts
                            </Typography>
                          </Box>
                        </>
                      )}
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                          {(isCopierPlan || isDediPlan) ? (selectedPlan.interval === 'month' ? 'Monthly' : selectedPlan.interval === 'quarter' ? 'Quarterly' : selectedPlan.interval === 'semiannual' ? 'Semi-annual' : 'Annual') + ' cost' : 'Cost'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                          ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      {creditBalance > 0 && (
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                            Available balance
                          </Typography>
                          <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                            ${creditBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      )}
                      <Divider />
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={700} fontSize={textSizeByBreakpoint(16)}>
                          To pay now
                        </Typography>
                        <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(16)} color="primary">
                          ${Math.max(0, totalCost - creditBalance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Stack>
                  </>
                  )}

                  {/* Step 3: Payment Method */}
                  {currentStep === 'payment' && selectedPlan && (
                  <>
                    {/* Payment Type Selection */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} mb={2} fontSize={textSizeByBreakpoint(16)}>
                        Payment type
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <Box
                          onClick={() => setPaymentType('card')}
                          sx={{
                            flex: 1,
                            border: `2px solid ${paymentType === 'card' ? theme.palette.primary.main : theme.palette.divider}`,
                            borderRadius: '8px',
                            p: 2.5,
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              borderColor: theme.palette.primary.main
                            }
                          }}
                        >
                          <CreditCardOutlined style={{ fontSize: '32px', color: paymentType === 'card' ? theme.palette.primary.main : theme.palette.text.secondary }} />
                          <Typography 
                            variant="body1" 
                            fontWeight={600} 
                            mt={1.5}
                            fontSize={textSizeByBreakpoint(14)}
                            color={paymentType === 'card' ? 'primary' : 'textPrimary'}
                          >
                            Credit/Debit Card
                          </Typography>
                        </Box>
                        <Box
                          onClick={() => setPaymentType('crypto')}
                          sx={{
                            flex: 1,
                            border: `2px solid ${paymentType === 'crypto' ? theme.palette.primary.main : theme.palette.divider}`,
                            borderRadius: '8px',
                            p: 2.5,
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              borderColor: theme.palette.primary.main
                            }
                          }}
                        >
                          <WalletOutlined style={{ fontSize: '32px', color: paymentType === 'crypto' ? theme.palette.primary.main : theme.palette.text.secondary }} />
                          <Typography 
                            variant="body1" 
                            fontWeight={600} 
                            mt={1.5}
                            fontSize={textSizeByBreakpoint(14)}
                            color={paymentType === 'crypto' ? 'primary' : 'textPrimary'}
                          >
                            Cryptocurrency
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Payment Method Selection */}
                    {paymentType === 'card' && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} mb={2} fontSize={textSizeByBreakpoint(16)}>
                          Payment method
                        </Typography>
                        {/* Deliberately OUTSIDE the add-card branch. No publishable key resolved
                            means Stripe.js was never loaded, so nothing on this step can confirm
                            an intent — the saved-card path included. Shown only inside the branch,
                            a user with a card on file saw a normal screen and learnt about it from
                            a snackbar AFTER "Complete Purchase" had minted an intent. */}
                        {stripeError && <StripeAccountUnavailable error={stripeError} retryable={stripeRetryable} onRetry={retryStripe} />}
                        {showAddCard ? (
                          <Box>
                            {/* The unavailable alert is hoisted above so it also covers the
                                saved-card path. This arm stays because without Stripe.js the
                                Loader below would spin forever. */}
                            {stripeError ? null : !clientSecret ? (
                              <Box display="flex" justifyContent="center" py={4}>
                                <Loader />
                              </Box>
                            ) : clientSecret && stripe && (
                              <Elements stripe={stripe} options={{ clientSecret }}>
                                <StripeCheckout
                                  handleEventChange={handleStripeChange}
                                  submit={false}
                                  loadCompleted={() => setStripeLoaded(true)}
                                  handleConfirmLoading={(loading: boolean) => setSaveCardLoading(loading)}
                                  saveCard={confirmSaveCard}
                                  saveCardResponse={handleCardSaved}
                                  handleSuccess={(success: boolean) => {
                                    if (success) {
                                      setShowAddCard(false);
                                    }
                                  }}
                                />
                              </Elements>
                            )}

                            <Stack direction="row" spacing={2} mt={3}>
                              <Button
                                variant="outlined"
                                onClick={() => setShowAddCard(false)}
                                fullWidth
                                sx={{ fontSize: textSizeByBreakpoint(14), py: 1.5 }}
                              >
                                Cancel
                              </Button>
                              <LoadingButton
                                variant="contained"
                                onClick={handleSaveCardClick}
                                disabled={!isCardComplete || !stripeLoaded}
                                loading={saveCardLoading}
                                fullWidth
                                sx={{ fontSize: textSizeByBreakpoint(14), py: 1.5 }}
                              >
                                Save Card
                              </LoadingButton>
                            </Stack>
                          </Box>
                        ) : (
                          <>
                            {saveCardLoading ? (
                              <Box display="flex" justifyContent="center" py={4}>
                                <Loader />
                              </Box>
                            ) : (
                              <>
                            <RadioGroup value={selectedCard} onChange={(e) => setSelectedCard(e.target.value)}>
                              <Stack spacing={1.5}>
                                {localPaymentCards.map((card: any) => (
                                  <Box
                                    key={card.id}
                                    onClick={() => setSelectedCard(card.paymentMethodId)}
                                    sx={{
                                      border: `2px solid ${selectedCard === card.paymentMethodId ? theme.palette.primary.main : theme.palette.divider}`,
                                      borderRadius: '8px',
                                      p: 2,
                                      cursor: 'pointer',
                                      backgroundColor: 'transparent',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        borderColor: theme.palette.primary.main
                                      }
                                    }}
                                  >
                                    <FormControlLabel
                                      value={card.paymentMethodId}
                                      control={<Radio />}
                                      sx={{ 
                                        width: '100%',
                                        m: 0,
                                        '& .MuiFormControlLabel-label': { flex: 1 }
                                      }}
                                      label={
                                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                          <Box display="flex" alignItems="center" gap={1.5} ml={1}>
                                            <img 
                                              src={iconMap[card.brand.toLowerCase()]} 
                                              alt={card.brand}
                                              style={{ height: '32px', width: 'auto', objectFit: 'contain', maxWidth: 'none' }}
                                            />
                                            <Box>
                                              <Typography variant="body1" fontWeight={600} fontSize={textSizeByBreakpoint(15)}>
                                                {card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} Ending {card.cardNumber}
                                              </Typography>
                                              <Typography variant="caption" color="textSecondary" fontSize={textSizeByBreakpoint(13)}>
                                                {card.brand.toLowerCase()}
                                              </Typography>
                                            </Box>
                                          </Box>
                                          {card.isDefault?.value && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: '4px',
                                                bgcolor: theme.palette.success.lighter,
                                                color: theme.palette.success.main,
                                                fontWeight: 600,
                                                fontSize: textSizeByBreakpoint(12)
                                              }}
                                            >
                                              Primary
                                            </Typography>
                                          )}
                                        </Box>
                                      }
                                    />
                                  </Box>
                                ))}
                              </Stack>
                            </RadioGroup>

                            <Button
                              variant="outlined"
                              startIcon={<CreditCardOutlined />}
                              fullWidth
                              onClick={() => setShowAddCard(true)}
                              sx={{ mt: 2, fontSize: textSizeByBreakpoint(14), py: 1.5 }}
                            >
                              Add New Card
                            </Button>
                            </>
                            )}
                          </>
                        )}
                      </Box>
                    )}

                    {/* Payment Info */}
                    {paymentType === 'crypto' && (
                      <Box
                        sx={{
                          p: 3,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                          You will be redirected to complete your crypto payment after confirming.
                        </Typography>
                      </Box>
                    )}

                    {/* Review your subscription */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} mb={2} fontSize={textSizeByBreakpoint(18)}>
                        Review your subscription
                      </Typography>
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: '12px',
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : theme.palette.grey[50],
                          border: `1px solid ${theme.palette.divider}`
                        }}
                      >
                        <Stack spacing={1.5}>
                          {(isCopierPlan || isDediPlan) && (
                            <>
                              {isDediPlan && (
                                <Box display="flex" justifyContent="space-between">
                                  <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                    First account
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                                    ${(selectedPlan.firstAccountPrice || selectedPlan.basePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{selectedPlan.interval === 'month' ? 'mo' : 'yr'}
                                  </Typography>
                                </Box>
                              )}
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                  {isDediPlan ? 'Each additional account' : 'Each extra account costs'}
                                </Typography>
                                <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                                  ${(isDediPlan ? (selectedPlan.additionalAccountPrice || selectedPlan.perAccount) : selectedPlan.basePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{selectedPlan.interval === 'month' ? 'mo' : selectedPlan.interval === 'quarter' ? 'qtr' : selectedPlan.interval === 'semiannual' ? 'semiann' : 'yr'}
                                </Typography>
                              </Box>
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                  Total accounts
                                </Typography>
                                <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                                  {accountsQuantity} Accounts
                                </Typography>
                              </Box>
                            </>
                          )}
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                              {(isCopierPlan || isDediPlan) ? (selectedPlan.interval === 'month' ? 'Monthly' : selectedPlan.interval === 'quarter' ? 'Quarterly' : selectedPlan.interval === 'semiannual' ? 'Semi-annual' : 'Annual') + ' cost' : 'Cost'}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                              ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                          {creditBalance > 0 && (
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary" fontSize={textSizeByBreakpoint(14)}>
                                Applied balance
                              </Typography>
                              <Typography variant="body2" fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
                                ${Math.min(creditBalance, totalCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                          )}
                          <Divider />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" fontWeight={700} fontSize={textSizeByBreakpoint(16)}>
                              To pay now
                            </Typography>
                            <Typography variant="body1" fontWeight={700} fontSize={textSizeByBreakpoint(16)} color="primary">
                              ${Math.max(0, totalCost - creditBalance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Box>
                  </>
                  )}
                </Box>

                {/* 3. Footer Section */}
                <Box
                  sx={{
                    mt: 'auto',
                    padding: { xl: 4.5, lg: 4, md: 3.5, sm: 3, xs: 2.5 },
                    paddingTop: 2,
                    borderTop: 'none'
                  }}
                >
                  <Stack spacing={1.5}>
                    {currentStep !== 'plan' && (
                      <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        onClick={() => setCurrentStep(currentStep === 'payment' ? 'configure' : 'plan')}
                        startIcon={<ArrowLeftOutlined />}
                        sx={{
                          height: '50px',
                          fontWeight: 600,
                          fontSize: textSizeByBreakpoint(16),
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          ':hover': {
                            borderColor: theme.palette.primary[400],
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                      >
                        Back To Subscriptions
                      </Button>
                    )}
                    <LoadingButton
                      fullWidth
                      variant="contained"
                      size="large"
                      type={currentStep === 'payment' ? 'submit' : 'button'}
                      loading={loading}
                      // `stripeError` blocks the CARD path only: the crypto branch of
                      // executePurchase never touches Stripe.js. Blocking here rather than
                      // at confirmPayment stops the backend minting an intent this client
                      // could never confirm.
                      disabled={
                        (currentStep === 'plan' && !values.selectedPlanId) ||
                        (currentStep === 'payment' && paymentType === 'card' && !!stripeError)
                      }
                      onClick={(e) => {
                        // Check if selected plan is overdue - if so, close drawer and trigger renew modal
                        if (currentStep === 'plan' && isOverduePlan(values.selectedPlanId)) {
                          e.preventDefault();
                          onClose();
                          onRenewOverdue?.();
                          return;
                        }
                        
                        if (currentStep === 'plan') {
                          e.preventDefault();
                          const isCopier = values.selectedPlanId.startsWith('copier-');
                          const isDedi = values.selectedPlanId.startsWith('analyzer-');
                          const isAnalytics = values.selectedPlanId.startsWith('analytics-');
                          setSelectedPlanType(isCopier ? 'copier' : isDedi ? 'dedi' : null);
                          if (isCopier) {
                            setStandardAccountsQuantity(2);
                            setPremiumAccountsQuantity(0);
                            setAccountsQuantity(2);
                          } else {
                            setAccountsQuantity(1);
                          }
                          // Skip configure step for Analyzer (flat fee, no quantity selection)
                          if (isAnalytics) {
                            setCurrentStep('payment');
                          } else {
                            setCurrentStep('configure');
                          }
                        } else if (currentStep === 'configure') {
                          e.preventDefault();
                          // For copier plans, sync total account quantity
                          if (isCopierPlan) {
                            setAccountsQuantity(standardAccountsQuantity + premiumAccountsQuantity);
                          }
                          setCurrentStep('payment');
                        }
                      }}
                      sx={{
                        backgroundColor: (theme) => currentStep === 'plan' && isOverduePlan(values.selectedPlanId) 
                          ? theme.palette.error.main 
                          : theme.palette.primary.main,
                        height: '50px',
                        fontWeight: 700,
                        fontSize: textSizeByBreakpoint(16),
                        ':hover': {
                          backgroundColor: (theme) => currentStep === 'plan' && isOverduePlan(values.selectedPlanId)
                            ? theme.palette.error.dark
                            : theme.palette.primary[400]
                        }
                      }}
                    >
                      {currentStep === 'plan' && isOverduePlan(values.selectedPlanId) 
                        ? 'Renew Subscription' 
                        : currentStep === 'plan' 
                          ? 'Continue' 
                          : currentStep === 'configure' 
                            ? 'Continue to Payment' 
                            : 'Complete Purchase'}
                    </LoadingButton>
                  </Stack>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
    </Drawer>
    </>
  );
};

export default UpgradePlanDrawer;
